
import { businessProgress, fundingRequirements, platformMetrics } from '@/integrations/supabase/client';

export interface BusinessProgress {
  id: string;
  business_name: string;
  development_stage: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface FundingRequirement {
  id: string;
  business_id: string;
  amount: number;
  next_step: string;
  fund_allocation_image: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformMetric {
  id: string;
  metric_name: string;
  metric_value: string;
  metric_category: string;
  created_at: string;
  updated_at: string;
}

/**
 * Service for handling business and funding related data
 */
class BusinessService {
  /**
   * Get all business progress records
   */
  async getBusinessProgress(): Promise<BusinessProgress[]> {
    try {
      const { data, error } = await businessProgress()
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data as BusinessProgress[];
    } catch (error) {
      console.error('Error fetching business progress:', error);
      throw error;
    }
  }
  
  /**
   * Create a new business progress record
   */
  async createBusinessProgress(businessData: Omit<BusinessProgress, 'id' | 'created_at' | 'updated_at'>): Promise<BusinessProgress> {
    try {
      const { data, error } = await businessProgress()
        .insert(businessData)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as BusinessProgress;
    } catch (error) {
      console.error('Error creating business progress:', error);
      throw error;
    }
  }
  
  /**
   * Update a business progress record
   */
  async updateBusinessProgress(id: string, businessData: Partial<BusinessProgress>): Promise<BusinessProgress> {
    try {
      const { data, error } = await businessProgress()
        .update(businessData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as BusinessProgress;
    } catch (error) {
      console.error(`Error updating business progress ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Delete a business progress record
   */
  async deleteBusinessProgress(id: string): Promise<void> {
    try {
      const { error } = await businessProgress()
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error(`Error deleting business progress ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Get all funding requirements
   */
  async getFundingRequirements(): Promise<FundingRequirement[]> {
    try {
      const { data, error } = await fundingRequirements()
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data as FundingRequirement[];
    } catch (error) {
      console.error('Error fetching funding requirements:', error);
      throw error;
    }
  }
  
  /**
   * Create a funding requirement
   */
  async createFundingRequirement(fundingData: Omit<FundingRequirement, 'id' | 'created_at' | 'updated_at'>): Promise<FundingRequirement> {
    try {
      const { data, error } = await fundingRequirements()
        .insert(fundingData)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as FundingRequirement;
    } catch (error) {
      console.error('Error creating funding requirement:', error);
      throw error;
    }
  }
  
  /**
   * Update a funding requirement
   */
  async updateFundingRequirement(id: string, fundingData: Partial<FundingRequirement>): Promise<FundingRequirement> {
    try {
      const { data, error } = await fundingRequirements()
        .update(fundingData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as FundingRequirement;
    } catch (error) {
      console.error(`Error updating funding requirement ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Delete a funding requirement
   */
  async deleteFundingRequirement(id: string): Promise<void> {
    try {
      const { error } = await fundingRequirements()
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error(`Error deleting funding requirement ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Get all platform metrics
   */
  async getPlatformMetrics(): Promise<PlatformMetric[]> {
    try {
      const { data, error } = await platformMetrics()
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data as PlatformMetric[];
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      throw error;
    }
  }
  
  /**
   * Create a new platform metric
   */
  async createPlatformMetric(metricData: Omit<PlatformMetric, 'id' | 'created_at' | 'updated_at'>): Promise<PlatformMetric> {
    try {
      const { data, error } = await platformMetrics()
        .insert(metricData)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as PlatformMetric;
    } catch (error) {
      console.error('Error creating platform metric:', error);
      throw error;
    }
  }
  
  /**
   * Update a platform metric
   */
  async updatePlatformMetric(id: string, metricData: Partial<PlatformMetric>): Promise<PlatformMetric> {
    try {
      const { data, error } = await platformMetrics()
        .update(metricData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return data as PlatformMetric;
    } catch (error) {
      console.error(`Error updating platform metric ${id}:`, error);
      throw error;
    }
  }
  
  /**
   * Delete a platform metric
   */
  async deletePlatformMetric(id: string): Promise<void> {
    try {
      const { error } = await platformMetrics()
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error(`Error deleting platform metric ${id}:`, error);
      throw error;
    }
  }
}

export default new BusinessService();
